import { Injectable, Optional, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

import { FEATURE_API_CONFIG, FeatureApiConfig } from '../../api/feature-flags-api.config';
import {
  FeatureFlag,
  FeatureFlagPayload,
  Status,
  EvaluationContext,
  EvaluationResponse,
} from '../../models/feature-flag.model';

/**
 * Response model for feature flags list
 */
interface FeatureFlagListResponse {
  flags: FeatureFlag[];
}

/**
 * Service for managing feature flags in the admin interface
 * Implements CRUD operations for feature flags
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsAdminService {
  private apiBaseUrl: string;

  constructor(
    private http: HttpClient,
    @Optional() @Inject(FEATURE_API_CONFIG) private config: FeatureApiConfig,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {
    this.apiBaseUrl = config?.apiBaseUrl || '/v1/feature-flags';
  }

  /**
   * Get all feature flags
   * @param status Optional status to filter flags by
   * @returns Observable of feature flags array
   */
  getAllFeatureFlags(status?: Status): Observable<FeatureFlag[]> {
    const url = status ? `${this.apiBaseUrl}?status=${status}` : this.apiBaseUrl;

    return this.http.get<FeatureFlagListResponse>(url).pipe(
      map(response => response.flags),
      catchError(error => {
        this.logger.error(`Error fetching feature flags: ${error?.message ?? error}`);
        return throwError(() => new Error('Failed to fetch feature flags'));
      })
    );
  }

  /**
   * Get a specific feature flag by ID
   * @param id Feature flag ID
   * @returns Observable of a single feature flag
   */
  getFeatureFlag(id: string): Observable<FeatureFlag> {
    return this.http.get<FeatureFlag>(`${this.apiBaseUrl}/${id}`).pipe(
      catchError(error => {
        this.logger.error(`Error fetching feature flag ${id}: ${error?.message ?? error}`);
        return throwError(() => new Error(`Feature flag ${id} not found`));
      })
    );
  }

  /**
   * Create a new feature flag
   * @param payload New feature flag data
   * @returns Observable of the created feature flag
   */
  createFeatureFlag(payload: FeatureFlagPayload): Observable<FeatureFlag> {
    return this.http.post<FeatureFlag>(this.apiBaseUrl, payload).pipe(
      catchError(error => {
        this.logger.error(`Error creating feature flag: ${error?.message ?? error}`);
        return throwError(() => new Error('Failed to create feature flag'));
      })
    );
  }

  /**
   * Update an existing feature flag
   * @param id Feature flag ID
   * @param payload Updated feature flag data
   * @returns Observable of the updated feature flag
   */
  updateFeatureFlag(id: string, payload: Partial<FeatureFlagPayload>): Observable<FeatureFlag> {
    return this.http.put<FeatureFlag>(`${this.apiBaseUrl}/${id}`, payload).pipe(
      catchError(error => {
        this.logger.error(`Error updating feature flag ${id}: ${error?.message ?? error}`);
        return throwError(() => new Error(`Failed to update feature flag ${id}`));
      })
    );
  }

  /**
   * Archive a feature flag
   * @param id Feature flag ID
   * @returns Observable of the archived feature flag
   */
  archiveFeatureFlag(id: string): Observable<FeatureFlag> {
    return this.http.post<FeatureFlag>(`${this.apiBaseUrl}/${id}/archive`, {}).pipe(
      catchError(error => {
        this.logger.error(`Error archiving feature flag ${id}: ${error?.message ?? error}`);
        return throwError(() => new Error(`Failed to archive feature flag ${id}`));
      })
    );
  }

  /**
   * Evaluate a feature flag for a given context
   * @param id Feature flag ID
   * @param context Evaluation context
   * @returns Observable of boolean indicating if the feature is enabled
   */
  evaluateFeatureFlag(id: string, context: EvaluationContext): Observable<boolean> {
    return this.http.post<EvaluationResponse>(`${this.apiBaseUrl}/${id}/evaluate`, { context }).pipe(
      map(response => response.enabled),
      catchError(error => {
        this.logger.error(`Error evaluating feature flag ${id}: ${error?.message ?? error}`);
        // Default to false for safety when evaluation fails
        return throwError(() => new Error(`Failed to evaluate feature flag ${id}`));
      })
    );
  }
}
